import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbContextType {
  items: BreadcrumbItem[];
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({ items: [] });

// Route name mapping
const routeNames: Record<string, string> = {
  '': 'Dashboard',
  'clients': 'Clients',
  'onboard': 'Onboard',
  'subscriptions': 'Subscriptions',
  'calendar': 'Renewal Calendar',
  'alerts': 'Alerts',
  'reports': 'Reports',
  'scheduled-reports': 'Security Reports',
  'audit-logs': 'Audit Log',
  'message-trace': 'Message Trace',
  'changelog': 'Changelog',
  'help': 'Help',
  'settings': 'Settings',
  'user': 'My Settings',
  'system': 'System Settings',
  'tenant-matcher': 'Tenant Matcher',
  'dev': 'Dev Tools',
  'email-test': 'Email Test',
};

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const items = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [{ label: 'Dashboard' }];

    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Dashboard', href: '/' }];
    let currentPath = '';

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;
      const isLast = i === segments.length - 1;

      // If it looks like an ID (UUID or long string), skip labeling it nicely
      const isId = segment.length > 20 || /^[0-9a-f-]{36}$/i.test(segment);

      const label = isId ? 'Detail' : (routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '));

      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath,
      });
    }

    return breadcrumbs;
  }, [location.pathname]);

  return (
    <BreadcrumbContext.Provider value={{ items }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs() {
  return useContext(BreadcrumbContext);
}
